import { Card, Table, Tag } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CURRENCY_SYMBOL,
  PRICER_TYPES,
  PRICING_MODES,
  PRICING_UNITS,
  TAG_COLORS,
} from "utils/constants";
import { summarizeRates } from "./pricingRatesDisplay";

const getPriceDisplay = (prices, t) => {
  if (!prices) return { value: 0, label: "-" };

  const type = get(prices, "type", "flat");
  const unit = get(prices, "unit", PRICING_UNITS.EUR);
  const suffix = unit === PRICING_UNITS.PMSS ? CURRENCY_SYMBOL.PMSS : "€";
  const perMonth = t("CONFIGURE.PRICING_SUMMARY.PER_MONTH");

  const rateRange = summarizeRates(prices);
  if (rateRange) {
    const { min, max } = rateRange;
    if (min === max) {
      return { value: min, label: `${min} ${suffix}` };
    }
    return {
      value: (min + max) / 2,
      label: `${min} - ${max} ${suffix}`,
    };
  }

  if (type === "flat") {
    const monthly = get(prices, "monthly", 0);
    return monthly
      ? { value: monthly, label: `${monthly} ${suffix}${perMonth}` }
      : { value: 0, label: "-" };
  }

  const base = get(prices, "base", {});
  const values = Object.values(base).filter((v) => v && v > 0);
  if (values.length === 0) return { value: 0, label: "-" };

  const min = Math.min(...values);
  const max = Math.max(...values);

  if (min === max) {
    return { value: min, label: `${min} ${suffix}${perMonth}` };
  }

  return {
    value: (min + max) / 2,
    label: `${min} - ${max} ${suffix}${perMonth}`,
  };
};

const getCoverageTotal = (coverage, t) => {
  const covPricingMode =
    get(coverage, "pricer.type") === PRICER_TYPES.COMPOSABLE
      ? PRICING_MODES.COMPOSABLE
      : PRICING_MODES.PACKAGED;

  if (covPricingMode === PRICING_MODES.PACKAGED) {
    return getPriceDisplay(get(coverage, "pricer.prices"), t).value;
  }

  const mappings = get(coverage, "coverageMappings", []);
  return mappings.reduce(
    (sum, m) => sum + getPriceDisplay(get(m, "pricer.prices"), t).value,
    0,
  );
};

const getCoveragePriceLabel = (coverage, t) => {
  const covPricingMode =
    get(coverage, "pricer.type") === PRICER_TYPES.COMPOSABLE
      ? PRICING_MODES.COMPOSABLE
      : PRICING_MODES.PACKAGED;

  if (covPricingMode === PRICING_MODES.PACKAGED) {
    return getPriceDisplay(get(coverage, "pricer.prices"), t).label;
  }

  const total = getCoverageTotal(coverage, t);
  return total > 0 ? `${total.toFixed(2)} €` : "-";
};

const PricingSummary = ({ coverages }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const totalPlanPrice = coverages.reduce(
    (sum, cov) => sum + getCoverageTotal(cov, t),
    0,
  );

  const dataSource = coverages.map((coverage) => {
    const category = get(coverage, "coverageCategory", {});
    const covPricingMode =
      get(coverage, "pricer.type") === PRICER_TYPES.COMPOSABLE
        ? PRICING_MODES.COMPOSABLE
        : PRICING_MODES.PACKAGED;
    const mappings = get(coverage, "coverageMappings", []);

    return {
      key: coverage.id,
      name: category.label || category.value,
      icon: category.icon,
      pricingMode: covPricingMode,
      priceLabel: getCoveragePriceLabel(coverage, t),
      benefits: mappings.length,
      children:
        covPricingMode === PRICING_MODES.COMPOSABLE
          ? mappings.map((m) => ({
              key: m.id,
              name: `${get(m, "coverage.type", "")}${get(m, "coverage.subtype") ? ` - ${get(m, "coverage.subtype")}` : ""}`,
              pricingMode: null,
              priceLabel: getPriceDisplay(get(m, "pricer.prices"), t).label,
              benefits: null,
            }))
          : undefined,
    };
  });

  return (
    <Card title={t("CONFIGURE.PRICING_SUMMARY.TITLE")} className="m-bottom-16">
      <Table
        dataSource={dataSource}
        pagination={false}
        size="small"
        columns={[
          {
            title: t("CONFIGURE.PRICING_SUMMARY.COLUMN_NAME"),
            dataIndex: "name",
            render: (name, record) => (
              <div className="d-flex align--center gap-8">
                {record.icon && (
                  <img
                    src={record.icon}
                    alt=""
                    className="configure-table-icon"
                  />
                )}
                <span
                  className={classNames("configure-name", {
                    "configure-name--normal": !record.children,
                  })}
                >
                  {name}
                </span>
              </div>
            ),
          },
          {
            title: t("CONFIGURE.PRICING_SUMMARY.COLUMN_MODE"),
            dataIndex: "pricingMode",
            width: 120,
            render: (mode) =>
              mode ? (
                <Tag
                  color={
                    mode === PRICING_MODES.COMPOSABLE
                      ? TAG_COLORS.GREEN
                      : TAG_COLORS.BLUE
                  }
                >
                  {mode === PRICING_MODES.COMPOSABLE
                    ? t("CONFIGURE.TAGS.COMPOSABLE")
                    : t("CONFIGURE.TAGS.PACKAGED")}
                </Tag>
              ) : null,
          },
          {
            title: t("CONFIGURE.PRICING_SUMMARY.COLUMN_BENEFITS"),
            dataIndex: "benefits",
            width: 80,
            render: (count) => (count != null ? count : null),
          },
          {
            title: t("CONFIGURE.PRICING_SUMMARY.COLUMN_PRICE"),
            dataIndex: "priceLabel",
            width: 160,
            render: (label) => (
              <span
                className={classNames("configure-step__price-value", {
                  "configure-step__price-value--positive": label !== "-",
                  "configure-step__price-value--muted": label === "-",
                })}
              >
                {label}
              </span>
            ),
          },
        ]}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell colSpan={3}>
              <strong>{t("CONFIGURE.PRICING_SUMMARY.TOTAL")}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              <strong
                className={classNames("configure-step__price-total", {
                  "configure-step__price-total--positive": totalPlanPrice > 0,
                  "configure-step__price-total--muted": totalPlanPrice <= 0,
                })}
              >
                {totalPlanPrice > 0
                  ? `${totalPlanPrice.toFixed(2)} ${t("CONFIGURE.PRICING_SUMMARY.PER_MONTH")}`
                  : "-"}
              </strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </Card>
  );
};

export default PricingSummary;
